import React, { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { GUID, PersonOrg } from '@ndvr/customer-api';

import { FormSheet } from '@/components/FormSheet';
import { useInternalUserProfilesQuery } from '@/modules/internal-user-queries';
import { usePersonSelector } from 'modules';
import { themeDefault } from 'styles';
import { getPersonName } from 'utils';
import { internalUserToCoreData, PersonCoreData } from 'views/person/person-queries';
import { FormInput, FormToggle, HorizontalLayout, IconClose, Text, Title, VerticalLayout } from 'widgets';

import { useUpdateFeatureFlagOverrides } from '../dev-queries';
import { useAdminPersonCoreData } from './feature-flag-person-queries';
import { type FeatureFlagWithOverrides } from './FeatureFlagList';

export const FlagDrawer = ({ onClose, flagData }: { onClose: () => void; flagData: FeatureFlagWithOverrides }) => {
    const { mutate: updateFeatureFlagOverrides, isPending } = useUpdateFeatureFlagOverrides();

    const onSubmit = useCallback(
        (formData: FeatureFlagWithOverrides, close: () => void) => {
            updateFeatureFlagOverrides({ updated: formData, original: flagData }, { onSuccess: close });
        },
        [flagData, updateFeatureFlagOverrides],
    );

    return (
        <FormSheet
            size="lg"
            title="Edit flag overrides"
            onClose={onClose}
            onSubmit={onSubmit}
            labelWidth="120px"
            defaultValues={flagData}
            pending={isPending}
        >
            <FormInput disabled name="featureFlag" label="Flag name" placeholder="" />
            <FormToggle
                name="globalOverride"
                label="Global override"
                description="Enabling this will set the flag value to true for all users. Useful for soft launching features."
            />
            <Overrides />
        </FormSheet>
    );
};

const Overrides = () => {
    const { watch, setValue } = useFormContext();
    const overridePersonOrgGUIDs: GUID<PersonOrg>[] = watch('overrides');

    const { data: appUsers, isLoading: isLoadingAppUsers } = useAdminPersonCoreData();
    const { data: internalProfiles, isLoading: isLoadingInternalUsers } = useInternalUserProfilesQuery();
    const internalUsers = useMemo(() => internalProfiles?.map(internalUserToCoreData), [internalProfiles]);

    const {
        all: selectedPersonProfiles,
        internal: internalOverrides,
        client: clientOverrides,
        unknown: unknownOverrides,
    } = useMemo(() => {
        const internal: PersonCoreData[] = [];
        const client: PersonCoreData[] = [];
        const unknown: PersonCoreData[] = [];
        if (!appUsers || !internalUsers) {
            return { all: [] as PersonCoreData[], internal, client, unknown };
        }
        for (const guid of overridePersonOrgGUIDs ?? []) {
            const internalMatch = internalUsers.find((u) => u.personOrgGUID === guid);
            if (internalMatch) {
                internal.push(internalMatch);
                continue;
            }
            const clientMatch = appUsers.find((u) => u.personOrgGUID === guid);
            if (clientMatch) {
                client.push(clientMatch);
                continue;
            }
            unknown.push({ personOrgGUID: guid });
        }
        return { all: [...internal, ...client, ...unknown], internal, client, unknown };
    }, [overridePersonOrgGUIDs, appUsers, internalUsers]);

    const {
        PersonSelector: AppUserSelector,
        selectedPersonProfile: appSelectedProfile,
        reset: resetApp,
    } = usePersonSelector({
        profiles: appUsers,
        isLoading: isLoadingAppUsers,
        excludeItems: selectedPersonProfiles,
    });
    const {
        PersonSelector: InternalUserSelector,
        selectedPersonProfile: internalSelectedProfile,
        reset: resetInternal,
    } = usePersonSelector({
        profiles: internalUsers,
        isLoading: isLoadingInternalUsers,
        excludeItems: selectedPersonProfiles,
    });

    useEffect(() => {
        const newSelection = appSelectedProfile ?? internalSelectedProfile;
        if (newSelection && !overridePersonOrgGUIDs.includes(newSelection.personOrgGUID)) {
            setValue('overrides', [...overridePersonOrgGUIDs, newSelection.personOrgGUID], {
                shouldDirty: true,
                shouldValidate: true,
            });
            resetApp();
            resetInternal();
        }
    }, [overridePersonOrgGUIDs, resetApp, resetInternal, appSelectedProfile, internalSelectedProfile, setValue]);

    return (
        <VerticalLayout $gap={24}>
            <Title section>Overrides</Title>
            <div className="border-border flex flex-col gap-2 rounded-md border p-3">
                <Title content>App users</Title>
                <Text helper $themeColor="subtle">
                    Clients and prospects using the mobile or client portal apps.
                </Text>
                <AppUserSelector />
            </div>
            <div className="border-border flex flex-col gap-2 rounded-md border p-3">
                <Title content>Internal users</Title>
                <Text helper $themeColor="subtle">
                    Claro staff with Command Center access.
                </Text>
                <InternalUserSelector />
            </div>

            <div className="flex flex-col gap-4">
                <Title content>Active overrides</Title>
                {internalOverrides.length > 0 && (
                    <OverrideBucket
                        label="Internal"
                        profiles={internalOverrides}
                        onRemove={(guid) =>
                            setValue(
                                'overrides',
                                overridePersonOrgGUIDs.filter((g) => g !== guid),
                                { shouldDirty: true, shouldValidate: true },
                            )
                        }
                    />
                )}
                {clientOverrides.length > 0 && (
                    <OverrideBucket
                        label="Client"
                        profiles={clientOverrides}
                        onRemove={(guid) =>
                            setValue(
                                'overrides',
                                overridePersonOrgGUIDs.filter((g) => g !== guid),
                                { shouldDirty: true, shouldValidate: true },
                            )
                        }
                    />
                )}
                {unknownOverrides.length > 0 && (
                    <OverrideBucket
                        label="Unknown"
                        profiles={unknownOverrides}
                        onRemove={(guid) =>
                            setValue(
                                'overrides',
                                overridePersonOrgGUIDs.filter((g) => g !== guid),
                                { shouldDirty: true, shouldValidate: true },
                            )
                        }
                    />
                )}
            </div>
        </VerticalLayout>
    );
};

const OverrideBucket = ({
    label,
    profiles,
    onRemove,
}: {
    label: string;
    profiles: PersonCoreData[];
    onRemove: (guid: GUID<PersonOrg>) => void;
}) => (
    <div className="flex flex-col gap-1">
        <Text helper $themeColor="subtle">
            {label}
        </Text>
        {profiles.map((profile) => (
            <OverrideItem key={profile.personOrgGUID} $rowCenter $gap={8}>
                <Text $truncated>
                    {getPersonName(profile)} [{profile.personOrgGUID}]
                </Text>
                <OverrideRemove
                    size="micro"
                    color={themeDefault.text.negative}
                    onClick={() => onRemove(profile.personOrgGUID)}
                />
            </OverrideItem>
        ))}
    </div>
);

const OverrideItem = styled(HorizontalLayout)`
    border: 1px solid ${({ theme }) => theme.border.light};
    border-radius: 3px;
    padding: 4px 8px;
    height: 22px;
    width: fit-content;
`;

const OverrideRemove = styled(IconClose)`
    cursor: pointer;
`;
